/* eslint-disable max-classes-per-file */
import * as d3 from 'd3'
import { binSize } from '../leaderboard/bat-speed-distribution-bin-size.ts'
import SimpleCurve from './simple-curve.ts'

const defaultMinBucket = 0
const defaultMaxBucket = 100

export const CURVE_INSTANCE_PROP = '__curve_instance'

const EXIT_STATE_PROP = '__exit_state'

// Tracks a curve node that is mid-exit so a quick re-add can reclaim it
// before handleExit removes it from the DOM.
type ExitState = { reclaimed: boolean }

// SVGGElement augmented with optional SimpleCurve instance and exit state
type CurveElement = SVGGElement & {
    [CURVE_INSTANCE_PROP]?: SimpleCurve
    [EXIT_STATE_PROP]?: ExitState
}

const transitionDuration = 378

export type ScaleType = 'n' | 'n_pct'

type DefaultOptions = {
    minBucket: number
    maxBucket: number
    bucketSize: number
    xTickValues?: number[]
    yTickValues?: number[]
    axisInfo: {
        xAxisTitle: string
        yAxisTitle: string
    }
    scaleType: ScaleType
}

export type UserRequiredOptions = {
    minBucket?: number
    maxBucket?: number
    bucketSize?: number
    xTickValues?: number[]
    yTickValues?: number[]
    axisInfo?: {
        xAxisTitle?: string
        yAxisTitle?: string
    }
    scaleType?: ScaleType
    height: number
    width?: number
    margin?: {
        top?: number
        right?: number
        bottom?: number
        left?: number
    }
}
export type SimpleChartOptions = {
    minBucket: number
    maxBucket: number
    bucketSize: number
    xTickValues?: number[]
    yTickValues?: number[]
    axisInfo: {
        xAxisTitle: string
        yAxisTitle: string
    }
    height: number
    width?: number
    margin?: {
        top?: number
        right?: number
        bottom?: number
        left?: number
    }
    scaleType?: ScaleType
}

export type BinData = {
    n: number
    n_pct: number
    bin: number
    // For added points particularly at beginning or end
    fake?: boolean
}

export type CurveInfo = {
    color: string
    curveType?: 'curveBasis' | 'curveLinear' | 'curveCardinal'
    data: BinData[]
    key: string
    split?: boolean
    strokeOnly?: boolean
    strokeWidth?: number
}
export type CurveOptions = Omit<CurveInfo, 'data'>

type ChartDims = {
    margin: {
        top: number
        right: number
        bottom: number
        left: number
    }
    height: number
    width: number
    chartHeight: number
    chartWidth: number
}

export default class SimpleChart<
    T extends UserRequiredOptions = SimpleChartOptions
> {
    scaleType: ScaleType = 'n'

    defaultOptions: DefaultOptions = {
        minBucket: defaultMinBucket,
        maxBucket: defaultMaxBucket,
        bucketSize: binSize,
        axisInfo: {
            xAxisTitle: 'X Axis',
            yAxisTitle: 'Y Axis',
        },
        scaleType: 'n_pct',
    }

    options: T & DefaultOptions

    dims: ChartDims = {
        margin: {
            top: 30,
            // Without this the right most border line doesn't show... Why?
            right: 1,
            bottom: 60,
            left: 60,
        },
        height: 600,
        width: 600,
        chartHeight: 510,
        chartWidth: 539,
    }

    private data: CurveInfo[] = []

    rawData: CurveInfo[] = []

    svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, undefined>

    chart

    curvesWrapper: d3.Selection<SVGGElement, unknown, HTMLElement, undefined>

    background: d3.Selection<SVGRectElement, unknown, HTMLElement, undefined>

    xScale: d3.ScaleLinear<number, number, never> = d3.scaleLinear()

    yScale: d3.ScaleLinear<number, number, never> = d3.scaleLinear()

    yMax: number = 1

    xAxisEl!: d3.Selection<SVGGElement, unknown, HTMLElement, undefined>

    yAxisEl!: d3.Selection<SVGGElement, unknown, HTMLElement, undefined>

    xAxisTitle!: d3.Selection<SVGTextElement, unknown, HTMLElement, undefined>

    yAxisTitle!: d3.Selection<SVGTextElement, unknown, HTMLElement, undefined>

    private curveInstances = new Map<string, SimpleCurve>()

    isLoading: boolean = false

    private pendingTransition: boolean = true

    private loadingSpinnerGroup!: d3.Selection<
        SVGGElement,
        unknown,
        HTMLElement,
        undefined
    >

    private readonly loadingSpinnerSize = 64

    constructor(
        el: d3.Selection<SVGSVGElement, unknown, HTMLElement, undefined>,
        initialData: CurveInfo[],
        options: T
    ) {
        this.svg = el.attr('class', 'distribution-curve-svg')

        // This doesn't really handle nested options well (axisInfo)
        this.options = { ...this.defaultOptions, ...options } as T &
            DefaultOptions
        this.dims.height = options.height || this.dims.height
        // Default width to height if not provided (makes square charts)
        this.dims.width = options.width || options.height || this.dims.width
        this.scaleType = options.scaleType || this.scaleType

        if (options.margin) {
            this.dims.margin = {
                ...this.dims.margin,
                ...options.margin,
            }
        }

        this.parseDims()

        this.chart = this.svg
            .append('g')
            .attr(
                'transform',
                `translate(${this.dims.margin.left}, ${this.dims.margin.top})`
            )

        this.curvesWrapper = this.chart
            .append('g')
            .classed('simple-viz-curves', true)

        this.parseData(initialData)

        this.updateScales()

        this.background = this.curvesWrapper
            .append('rect')
            .attr('fill', 'white')
            .attr('height', this.dims.chartHeight)
            .attr('width', this.dims.chartWidth)

        this.createLoadingSpinner()

        this.drawAxes()

        this.updateCurves(false) // No animation on initial load
    }

    parseData(rawData: CurveInfo[]) {
        this.rawData = rawData
        this.data = rawData.map(({ data, ...rest }) => ({
            data: this.filterData(data),
            ...rest,
        }))
    }

    createLoadingSpinner() {
        const cx = this.dims.chartWidth / 2
        const cy = this.dims.chartHeight / 2
        const halfSize = this.loadingSpinnerSize / 2

        this.loadingSpinnerGroup = this.chart
            .append('g')
            .attr('class', 'simple-chart-loading-spinner')
            .attr('transform', `translate(${cx}, ${cy})`)
            .attr('opacity', 0.6)
            .style('display', 'none')
            .style('pointer-events', 'none')

        this.loadingSpinnerGroup
            .append('image')
            .attr('class', 'simple-chart-loading-spinner-image')
            .attr('x', -halfSize)
            .attr('y', -halfSize)
            .attr('width', this.loadingSpinnerSize)
            .attr('height', this.loadingSpinnerSize)
            .attr('href', '/site-core/images/savant-logo.svg')
    }

    startLoadingSpinner() {
        this.loadingSpinnerGroup.style('display', null)
    }

    stopLoadingSpinner() {
        this.loadingSpinnerGroup.style('display', 'none')
    }

    setLoading(isLoading: boolean): Promise<boolean> {
        this.isLoading = isLoading

        // add spinner if loading
        if (isLoading) {
            this.startLoadingSpinner()

            return (
                this.curvesWrapper
                    .transition()
                    .attr('opacity', 0.5)
                    .end()
                    .then(() => true)
                    // Swallow interrupt errors
                    .catch(() => true)
            )
        }

        this.stopLoadingSpinner()
        return Promise.all([
            this.curvesWrapper.transition().attr('opacity', 1).end(),
            this.drawData(this.pendingTransition),
        ]).then(() => true)
    }

    updateCurves(transition = true) {
        const duration = transition ? transitionDuration : 0
        let enterTransitionPromise: Promise<void> | undefined
        const updateTransitionPromises: Promise<void>[] = []

        // Capture exiting nodes BEFORE the join processes them
        const exitingNodes = this.curvesWrapper
            .selectAll<SVGGElement, CurveInfo>('g.curve')
            .data(this.data, (d) => d.key)
            .exit()
            .nodes() as CurveElement[]

        // Tag exiting nodes so a re-add of the same key (before the exit
        // transition finishes removing the node) can reclaim them.
        exitingNodes.forEach((node) => {
            // eslint-disable-next-line no-param-reassign
            node[EXIT_STATE_PROP] = { reclaimed: false }
        })

        // Now run the join (exit just returns selection, removal handled below)
        this.curvesWrapper
            .selectAll<SVGGElement, CurveInfo>('g.curve')
            .data(this.data, (d) => d.key)
            .join(
                (enter) => {
                    const g = enter
                        .append('g')
                        .attr('class', 'curve')
                        .attr('opacity', 0)
                        .attr('id', (d) => `curve-${d.key}`)

                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                    const that = this

                    g.each(function handleEach({ data, ...options }) {
                        const instance = new SimpleCurve(
                            d3.select(this),
                            data,
                            that.xScale,
                            that.yScale,
                            that.scaleType,
                            options
                        )
                        this[CURVE_INSTANCE_PROP] = instance
                        that.curveInstances.set(options.key, instance)
                    })

                    if (duration > 0) {
                        enterTransitionPromise = g
                            .transition()
                            .duration(duration)
                            .attr('opacity', 1)
                            .end()
                    } else {
                        g.attr('opacity', 1)
                    }

                    return g
                },
                (update) => {
                    // eslint-disable-next-line @typescript-eslint/no-this-alias
                    const that = this

                    update.each(function handleEach({ data, ...options }) {
                        if (!this || !this[CURVE_INSTANCE_PROP]) {
                            return
                        }

                        // Reclaim a node that is mid-exit: stop the fade and
                        // restore full opacity so it stays visible instead of
                        // being removed by the in-flight handleExit.
                        // Mark reclaimed (don't delete it - handleExit reads
                        // the same reference to skip removal). A later genuine
                        // exit re-tags the node with a fresh state.
                        const node = this as CurveElement
                        const exitState = node[EXIT_STATE_PROP]
                        if (exitState) {
                            exitState.reclaimed = true
                            d3.select(node).interrupt().attr('opacity', 1)
                        }

                        const result = this[CURVE_INSTANCE_PROP].updateCurve(
                            data,
                            that.xScale,
                            that.yScale,
                            that.scaleType,
                            options,
                            transition
                        )

                        updateTransitionPromises.push(result)
                    })

                    return update
                },
                (exit) => exit // Don't remove here - handled separately below
            )

        return (
            Promise.all([
                enterTransitionPromise,
                ...updateTransitionPromises,
                handleExit(exitingNodes, transition, duration),
            ])
                .then(() => true)
                // Swallow errors, but I think there actually shouldn't be any errors since we catch the interrupts earlier?
                .catch((err) => {
                    console.error('Error in updateCurves transitions', err)
                    return true
                })
        )
    }

    updateData(rawData: CurveInfo[], transition = true) {
        this.parseData(rawData)
        this.pendingTransition = transition

        if (this.isLoading) {
            return Promise.resolve(true)
        }

        return this.drawData(transition)
    }

    drawData(transition = true) {
        this.updateScales()
        this.updateAxes(transition)
        return this.updateCurves(transition)
    }

    addData(data: CurveInfo, transition = true) {
        const keys = this.rawData.map((d) => d.key)
        if (keys.includes(data.key)) {
            // Run an update?
            throw new Error('duplicate key')
        }

        const newRawData = [...this.rawData, data]

        return this.updateData(newRawData, transition)
    }

    removeData(key: string, transition = true) {
        const filteredData = this.rawData.filter((d) => d.key !== key)

        if (filteredData.length === this.rawData.length) {
            console.warn(`Couldn't find key ${key} to remove`)
        }

        return this.updateData(filteredData, transition)
    }

    setHoveredCurve(key: string | null) {
        this.curvesWrapper
            .selectAll<SVGGElement, CurveInfo>('g.curve')
            .each(function handleHover(curve) {
                const instance = this[CURVE_INSTANCE_PROP]

                if (!instance) {
                    return
                }

                if (key === null) {
                    instance.removeHighlight()
                    return
                }

                if (curve.key === key) {
                    d3.select(this).raise()
                    instance.highlight()
                    return
                }

                instance.deemphasize()
            })
    }

    updateScaleType(scaleType: ScaleType, transition = true) {
        this.scaleType = scaleType

        return this.updateData(this.rawData, transition)
    }

    filterData(bins: BinData[]) {
        const filteredBins = bins.filter(
            (d) =>
                d.bin >= this.options.minBucket &&
                d.bin <= this.options.maxBucket
        )

        const { minBucket, maxBucket, bucketSize } = this.options

        const bufferedPoints: BinData[] = [
            {
                bin: minBucket - bucketSize / 2,
                n: 0,
                n_pct: 0,
                fake: true,
            },
        ]

        let nextIdx = 0
        for (let i = minBucket; i <= maxBucket; i += bucketSize) {
            if (filteredBins[nextIdx]?.bin === i) {
                bufferedPoints.push(filteredBins[nextIdx])
                nextIdx += 1
            } else {
                bufferedPoints.push({ bin: i, n: 0, n_pct: 0, fake: true })
            }
        }
        bufferedPoints.push({
            bin: maxBucket + bucketSize / 2,
            n: 0,
            n_pct: 0,
            fake: true,
        })

        // Want to have a row for each possible bin, plus an extra 0 before and after
        return bufferedPoints
    }

    parseDims() {
        const { height, width, margin } = this.dims
        this.dims.chartHeight = height - margin.top - margin.bottom
        this.dims.chartWidth = width - margin.left - margin.right
    }

    updateScales() {
        const { minBucket, maxBucket, bucketSize } = this.options
        this.xScale = d3
            .scaleLinear()
            .domain([minBucket - bucketSize / 2, maxBucket + bucketSize / 2])
            .range([0, this.dims.chartWidth])

        this.getYMax()

        this.yScale = d3
            .scaleLinear()
            .domain([this.yMax, 0])
            .range([0, this.dims.chartHeight])
            .nice()
    }

    getYMax() {
        if (!this.data.length && !this.yMax) {
            const defaultMax = this.scaleType === 'n_pct' ? 1 : 100
            this.yMax = defaultMax
            return
        }
        if (!this.data.length) {
            if (this.yMax > 1 && this.scaleType === 'n_pct') {
                this.yMax = 1
            } else if (this.yMax <= 1 && this.scaleType === 'n') {
                this.yMax = 100
            }
            return
        }

        const maxes = this.data.map(({ data }) =>
            Math.max(...data.map((d) => d[this.scaleType]))
        )

        // 3% buffer, scientifically chosen
        this.yMax = 1.03 * Math.max(...maxes)
    }

    drawAxes() {
        let xAxis = d3
            .axisBottom(this.xScale)
            .tickSize(-1 * this.dims.chartHeight)
            .tickFormat((d) => String(Math.abs(Number(d))))
            .ticks(5)

        if (this.options.xTickValues) {
            xAxis = xAxis.tickValues(this.options.xTickValues)
        }

        let yAxis = d3
            .axisLeft(this.yScale)
            .tickFormat(this.getTickFormat())
            .tickSize(-1 * this.dims.chartWidth)
            .ticks(5)

        if (this.options.yTickValues) {
            yAxis = yAxis.tickValues(this.options.yTickValues)
        }

        this.xAxisEl = this.chart
            .append('g')
            .attr('class', 'axis x-axis')
            .attr('transform', `translate(0, ${this.dims.chartHeight})`)
            .call(xAxis)
            .call((el) => {
                el.selectAll('.tick line')
                    .attr('stroke-opacity', 0.2)
                    .attr('stroke-dasharray', '5,5')
                el.selectAll<SVGTextElement, unknown>('.tick text').attr(
                    'dy',
                    12
                )
            })

        this.yAxisEl = this.chart
            .append('g')
            .attr('class', 'axis y-axis')
            .call(yAxis)
            .call((el) => {
                el.selectAll('.tick line')
                    .attr('stroke-opacity', 0.2)
                    .attr('stroke-dasharray', '5,5')
            })

        this.xAxisTitle = this.chart
            .append('text')
            .attr('class', 'xLabel')
            .attr(
                'transform',
                `translate(${this.dims.chartWidth / 2} ,${
                    this.dims.chartHeight + 35
                })`
            )
            .style('text-anchor', 'middle')
            .style('font-size', '1rem')
            .text(this.options.axisInfo.xAxisTitle)

        this.yAxisTitle = this.chart
            .append('text')
            .attr('class', 'yLabel')
            .attr('transform', 'rotate(-90)')
            .attr('y', 0 - this.dims.margin.left / 2 - 7)
            .attr('x', 0 - this.dims.chartHeight / 2)
            .attr('dy', '0em')
            .style('text-anchor', 'middle')
            .style('font-size', '1rem')
            .text(this.options.axisInfo.yAxisTitle)
    }

    updateAxes(transition = true) {
        let xAxis = d3
            .axisBottom(this.xScale)
            .tickSize(-1 * this.dims.chartHeight)
            // Thanks TypeScript
            .tickFormat((d) => String(Math.abs(Number(d))))
            .ticks(5)

        if (this.options.xTickValues) {
            xAxis = xAxis.tickValues(this.options.xTickValues)
        }

        let yAxis = d3
            .axisLeft(this.yScale)
            .tickFormat(this.getTickFormat())
            .tickSize(-1 * this.dims.chartWidth)
            .ticks(5)

        if (this.options.yTickValues) {
            yAxis = yAxis.tickValues(this.options.yTickValues)
        }

        if (transition) {
            this.xAxisEl.transition().duration(transitionDuration).call(xAxis)

            this.yAxisEl.transition().duration(transitionDuration).call(yAxis)
        } else {
            this.xAxisEl.call(xAxis)
            this.yAxisEl.call(yAxis)
        }

        // Style ticks (works on current DOM state)
        this.xAxisEl
            .selectAll('.tick line')
            .attr('stroke-opacity', 0.2)
            .attr('stroke-dasharray', '5,5')
        this.xAxisEl
            .selectAll<SVGTextElement, unknown>('.tick text')
            .attr('dy', 12)

        this.yAxisEl
            .selectAll('.tick line')
            .attr('stroke-opacity', 0.2)
            .attr('stroke-dasharray', '5,5')

        this.xAxisTitle.text(this.options.axisInfo.xAxisTitle)
        this.yAxisTitle.text(this.options.axisInfo.yAxisTitle)
    }

    updateYAxisTitle(title: string) {
        this.options.axisInfo.yAxisTitle = title
    }

    getTickFormat() {
        switch (this.scaleType) {
            case 'n_pct':
                return d3.format('.0%')
            default:
                return (d: d3.NumberValue) => String(d)
        }
    }
}

// Handle exit: zeroCurve first, then fade and remove
async function handleExit(
    exitingNodes: CurveElement[],
    transition: boolean,
    duration: number
) {
    if (exitingNodes.length === 0) {
        return
    }

    // First: animate curves to zero
    if (transition) {
        const zeroPromises = exitingNodes
            .filter((node) => node[CURVE_INSTANCE_PROP])
            .map((node) => node[CURVE_INSTANCE_PROP]!.zeroCurve(duration))
        // Swallow all interrupts
        await Promise.all(zeroPromises).catch(() => true)
    }

    // Then: fade out and remove the container. Skip any nodes that were
    // reclaimed by a re-add while the zero transition was running.
    const survivors = exitingNodes.filter(
        (node) => !node[EXIT_STATE_PROP]?.reclaimed
    )
    const selection = d3.selectAll(survivors)

    if (transition && duration > 0) {
        await selection
            .transition()
            .duration(duration)
            .attr('opacity', 0)
            .remove()
            .end()
            // Swallow interrupt errors
            .catch(() => true)
    } else {
        selection.remove()
    }
}
